import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import api from '@/apiRequest';
import ServiceComponent from '@/components/MarketplacePage/Service/ServiceComponent';
import { useUser } from '@/contexts/UserContext';
import { showToast } from '@/store/ducks/toasts';

export const resolveDefaultHooks = (formSchema, existingHooks) => {
  if (Array.isArray(existingHooks) && existingHooks.length > 0) {
    return existingHooks;
  }

  const hooksSchema = formSchema?.properties?.hooks;
  if (hooksSchema?.type !== 'array') return existingHooks || [];

  const items = hooksSchema.items || {};
  const refName = items.$ref?.split('/').pop() || 'hook';
  const definitions = formSchema?.definitions || {};

  // Resolve definition: direct key match, case-insensitive match, or fallback to items
  const definition =
    definitions[refName] ||
    Object.entries(definitions).find(([key]) => key.toLowerCase() === refName.toLowerCase())?.[1] ||
    items;

  const typeSchema = definition?.properties?.type || items?.properties?.type;
  const valueSchema = definition?.properties?.value || items?.properties?.value;

  const defaultType = typeSchema?.enum?.[0] || typeSchema?.default || 'webhook';
  const defaultValue = valueSchema?.default || '';

  return [{ type: defaultType, value: defaultValue }];
};

const ServiceContainer = () => {
  const { refreshMe } = useUser();
  const [service, setService] = useState(null);
  const [formData, setFormData] = useState({});
  const [busy, setBusy] = useState(true);
  const [formBusy, setFormBusy] = useState(false);
  const { service: serviceSlug } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();
  const location = useLocation();
  const stateType = location?.state?.type;

  // Dual Reparo Fácil States
  const [marketplaceService, setMarketplaceService] = useState(null);
  const [marketplaceFormData, setMarketplaceFormData] = useState({});
  const [erpService, setErpService] = useState(null);
  const [erpFormData, setErpFormData] = useState({});

  const type = useMemo(() => {
    if (serviceSlug === 'jetimob') return 'erp';
    if (stateType) return stateType;
    const types = JSON.parse(window.sessionStorage.getItem('marketplaceServiceTypes') || '{}');
    return types[serviceSlug];
  }, [stateType, serviceSlug]);

  const schema = useMemo(
    () => ({
      type: 'object',
      required: ['name'],
      properties: {
        name: {
          type: 'string',
          title: intl.formatMessage({ id: 'NAME' }),
          description: intl.formatMessage({ id: 'PLATFORM_NAME_FORM_MESSAGE' }),
        },
      },
    }),
    [intl]
  );

  const reparoFacilSchema = useMemo(
    () => ({
      type: 'object',
      required: ['apiKey'],
      properties: {
        apiKey: {
          type: 'string',
          title: 'Api Key',
        },
      },
    }),
    []
  );

  const isReparoFacil = serviceSlug === 'reparofacil' || serviceSlug === 'reparo_facil';
  const isJetimob = serviceSlug === 'jetimob';

  useEffect(() => {
    const me = JSON.parse(window.sessionStorage.getItem('me'));
    const hasReparoFacilFranchisee = me?.profile?.franchisee_features?.includes('ORDER_MARKETPLACE_REPARO_FACIL');

    if (isReparoFacil && !hasReparoFacilFranchisee) {
      navigate('/marketplace');
      return;
    }

    const fetchService = async () => {
      try {
        setBusy(true);
        if (isReparoFacil) {
          const [marketplaceRes, erpRes] = await Promise.all([
            api.get('/marketplace/reparofacil'),
            api.get('/erp/services/reparo_facil'),
          ]);
          setMarketplaceService(marketplaceRes.data);
          setMarketplaceFormData(marketplaceRes.data?.data || {});
          setErpService(erpRes.data);
          setErpFormData(erpRes.data?.data || {});
          // Set primary service state for logo, descriptions etc.
          setService(marketplaceRes.data);
        } else {
          const endpoint = type === 'erp' ? `/erp/services/${serviceSlug}` : `/marketplace/${serviceSlug}`;
          const { data } = await api.get(endpoint);
          setService(data);
          const initialFormData = { ...(data?.data || {}) };
          if (type === 'erp') {
            const hooks = resolveDefaultHooks(data?.schema?.form, initialFormData.hooks);
            if (hooks.length > 0) {
              initialFormData.hooks = hooks;
            }
          }
          setFormData(initialFormData);
        }
        setBusy(false);
      } catch (err) {
        dispatch(
          showToast({
            type: 'error',
            title: intl.formatMessage({ id: 'ERROR' }),
            text: intl.formatMessage({ id: 'SERVICE_LOAD_ERROR' }),
            duration: 5000,
          })
        );
        navigate('/marketplace');
        console.log(err);
      }
    };

    fetchService();
  }, [dispatch, navigate, serviceSlug, location, type, intl, isReparoFacil]);

  const handleDisableClick = async () => {
    try {
      if (type === 'erp') {
        await api.delete(`/erp/services/${serviceSlug}`);
      } else {
        await api.delete(`/marketplace/${serviceSlug}`);
      }
      setFormData({
        hooks: [],
        apiKey: '',
      });
      setService(oldState => ({
        ...oldState,
        enabled: false,
        status: 'AVAILABLE',
        data: {
          ...(oldState?.data || {}),
          apiKey: '',
          api_key: '',
        },
      }));
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS' }),
          text: `${intl.formatMessage({
            id: 'INTEGRATION_WITH_SERVICE_MESSAGE',
          })} ${service.name} ${intl.formatMessage({
            id: 'HAS_BEEN_DEACTIVATED_MESSAGE',
          })}`,
          duration: 5000,
        })
      );
      await refreshMe();
    } catch (err) {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: `${intl.formatMessage({ id: 'SERVICE_DEACTIVATION_ERROR' })} ${service.name}, ${intl.formatMessage({
            id: 'TRY_AGAIN_LATER_MESSAGE',
          })}`,
          duration: 5000,
        })
      );
    }
  };

  const handleChange = ({ formData: data }) => {
    setFormData(data);
  };

  const handleSubmit = async ({ formData: values }) => {
    const isEditing = service.enabled || service.status === 'ENABLED';
    const action = isEditing ? 'edit' : 'create';
    const isErp = type === 'erp';
    const endpoint = isErp ? '/erp/services' : '/marketplace';
    const payload = isErp
      ? {
          ...values,
          client: service.slug,
          hooks: resolveDefaultHooks(service?.schema?.form, values.hooks),
        }
      : {
          item: serviceSlug,
          ...values,
        };

    try {
      setFormBusy(true);
      let data;
      if (action === 'create') {
        ({ data } = await api.post(endpoint, payload));
      } else {
        await api.put(`${endpoint}/${service.public_id || service.slug}`, payload);
      }

      if (!isErp) {
        setService(oldState => ({ ...oldState, enabled: true }));
      } else {
        const newApiKey = data?.api_key || data?.apiKey || data?.data?.apiKey || data?.data?.api_key;
        setService(oldState => ({
          ...oldState,
          status: 'ENABLED',
          enabled: true,
          ...(newApiKey
            ? {
                data: {
                  ...(oldState?.data || {}),
                  apiKey: newApiKey,
                  hooks: payload.hooks,
                },
              }
            : {}),
        }));
        if (action === 'create') {
          setFormData(oldState => ({
            ...oldState,
            apiKey: newApiKey,
            hooks: payload.hooks,
          }));
        }
      }

      const text =
        action === 'create'
          ? `${intl.formatMessage({
              id: 'INTEGRATION_WITH_SERVICE_MESSAGE',
            })} ${service.name} ${intl.formatMessage({
              id: 'WAS_SUCCESSFULLY_DONE_MESSAGE',
            })}`
          : `${intl.formatMessage({
              id: 'INTEGRATION_WITH_SERVICE_MESSAGE',
            })} ${service.name} ${intl.formatMessage({
              id: 'SUCCESSFULLY_UPDATED_MESSAGE',
            })}`;
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS' }),
          text,
          duration: 5000,
        })
      );
      await refreshMe();
    } catch (err) {
      const text =
        action === 'create'
          ? `${intl.formatMessage({
              id: 'SERVICE_INTEGRATION_ACTIVATION_ERROR',
            })} ${service.name}, ${intl.formatMessage({
              id: 'TRY_AGAIN_LATER_MESSAGE',
            })}`
          : `${intl.formatMessage({
              id: 'SERVICE_INTEGRATION_UPDATE_ERROR',
            })} ${service.name}, ${intl.formatMessage({
              id: 'TRY_AGAIN_LATER_MESSAGE',
            })}`;
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text,
          duration: 5000,
        })
      );
    } finally {
      setFormBusy(false);
    }
  };

  // Dual Reparo Fácil Handlers
  const handleMarketplaceDisable = async () => {
    try {
      await api.delete('/marketplace/reparofacil');
      setMarketplaceFormData({ apiKey: '' });
      setMarketplaceService(old => ({ ...old, enabled: false }));
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS' }),
          text: 'Integração desativada com sucesso!',
          duration: 5000,
        })
      );
      await refreshMe();
    } catch (err) {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: 'Erro ao desativar integração.',
          duration: 5000,
        })
      );
    }
  };

  const handleErpDisable = async () => {
    try {
      await api.delete('/erp/services/reparo_facil');
      setErpFormData({ hooks: [], apiKey: '' });
      setErpService(old => ({ ...old, status: 'AVAILABLE', enabled: false }));
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS' }),
          text: 'Chave de acesso desativada com sucesso!',
          duration: 5000,
        })
      );
      await refreshMe();
    } catch (err) {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: 'Erro ao desativar chave de acesso.',
          duration: 5000,
        })
      );
    }
  };

  const handleMarketplaceSubmit = async ({ formData: values }) => {
    let action = 'create';
    if (marketplaceService?.enabled) {
      action = 'edit';
    }
    try {
      setFormBusy(true);
      if (action === 'create') {
        await api.post('/marketplace', { item: 'reparofacil', ...values });
      } else {
        await api.put('/marketplace/reparofacil', { item: 'reparofacil', ...values });
      }
      setMarketplaceService(old => ({ ...old, enabled: true }));
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS' }),
          text: 'Integração ativada com sucesso!',
          duration: 5000,
        })
      );
      await refreshMe();
    } catch (err) {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: 'Erro ao salvar integração.',
          duration: 5000,
        })
      );
    } finally {
      setFormBusy(false);
    }
  };

  const handleErpSubmit = async ({ formData: values }) => {
    let action = 'create';
    if (erpService?.enabled || erpService?.status === 'ENABLED') {
      action = 'edit';
    }
    try {
      setFormBusy(true);
      let data;
      if (action === 'create') {
        ({ data } = await api.post('/erp/services', { ...values, client: 'reparo_facil' }));
      } else {
        await api.put('/erp/services/reparo_facil', values);
      }
      setErpService(old => ({ ...old, status: 'ENABLED', enabled: true }));
      if (action === 'create') {
        setErpFormData(oldState => ({ ...oldState, apiKey: data.api_key }));
      }
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS' }),
          text: 'Chave de acesso gerada com sucesso!',
          duration: 5000,
        })
      );
      await refreshMe();
    } catch (err) {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: 'Erro ao gerar chave de acesso.',
          duration: 5000,
        })
      );
    } finally {
      setFormBusy(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    dispatch(
      showToast({
        type: 'success',
        title: intl.formatMessage({ id: 'SUCCESS' }),
        text: intl.formatMessage({
          id: 'marketplace.apiKey.copiedToast',
          defaultMessage: 'API Key copiada para a área de transferência!',
        }),
        duration: 3000,
      })
    );
  };

  const handleError = errors => {
    console.log('errors', errors);
  };

  const uiSchema = useMemo(() => {
    if (serviceSlug === 'reparofacil') return {};
    if (type === 'erp' && (service?.enabled || service?.status === 'ENABLED')) {
      return { apiKey: { 'ui:disabled': true } };
    }
    return {};
  }, [serviceSlug, type, service]);

  return (
    <ServiceComponent
      busy={busy}
      formBusy={formBusy}
      schema={serviceSlug === 'reparofacil' ? reparoFacilSchema : service?.schema?.form || schema}
      formData={formData}
      isReparoFacil={isReparoFacil}
      isJetimob={isJetimob}
      uiSchema={uiSchema}
      onChange={handleChange}
      onDisableClick={handleDisableClick}
      onSubmit={handleSubmit}
      onError={handleError}
      service={service}
      type={type}
      intl={intl}
      // Dual Reparo Fácil Props
      marketplaceService={marketplaceService}
      marketplaceFormData={marketplaceFormData}
      onMarketplaceSubmit={handleMarketplaceSubmit}
      onMarketplaceChange={({ formData: d }) => setMarketplaceFormData(d)}
      onMarketplaceDisable={handleMarketplaceDisable}
      erpService={erpService}
      erpFormData={erpFormData}
      onErpSubmit={handleErpSubmit}
      onErpChange={({ formData: d }) => setErpFormData(d)}
      onErpDisable={handleErpDisable}
      onCopy={handleCopy}
    />
  );
};

export default ServiceContainer;
